import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';
import { useOrganizations } from './useOrganizations';
import { config } from 'config';

export const useWebsites = () => {
  const { user } = useAuth();
  const { organization, website, setWebsite } = useOrganizations();
  const [options, setOptions] = useState<{ value: number; label: string }[] | null>(null);
  const [rechecking, setRechecking] = useState(false);
  const queryClient = useQueryClient();
  const apiClient = useClient();
  const ganClient = useClient(config.GAN_URL);

  useEffect(() => {
    if (user) {
      if (organization) {
        const { websites } = organization;
        setOptions(websites.map((org: any) => ({ value: org.id, label: org.url })));
        if (user.company.type === 'Client Account') {
          if (!website) {
            const searchParams = new URLSearchParams(window.location.search);
            const qsWebsite = searchParams.get('ws');
            setWebsite(+qsWebsite! || websites[0]?.id);
          }
        }
      } else {
        setOptions([]);
      }
    }
  }, [organization]);

  /**
   * Throws a message meant for a person, not a stack trace.
   *
   * `new URL(url)` throws a bare "Invalid URL" for anything unparseable, and nothing
   * caught it - so typing "example.com" logged to the console and the dialog just sat
   * there as though the click had not registered. A protocol is the usual thing
   * missing, so the message says so.
   */
  const createWebsite = async (values: any) => {
    const { url } = values;

    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      throw new Error(`"${url}" is not a valid URL. Include the protocol, for example https://example.com`);
    }

    if (!parsed.protocol.startsWith('http')) {
      throw new Error('A website URL has to start with http:// or https://');
    }

    const { protocol, host, pathname } = parsed;
    const payload = {
      companyId: organization?.id,
      parentCompanyId: organization?.parentCompanyId,
      url: `${protocol}//${host}${pathname}`,
    };
    const newWebsite = await apiClient.post('/websites', payload);
    queryClient.invalidateQueries({ queryKey: ['WEBSITES'] });
    setTimeout(() => setWebsite(newWebsite.id), 50);
  };

  const refreshPropertyTags = async (website: any) => {
    const url = `/google-analytics/refresh-property-tags/${website.ganPropertyId}`;
    await ganClient.post(url);
    queryClient.invalidateQueries({ queryKey: ['WEBSITES'] });
  };

  const deleteWebsite = async (id: number) => {
    await apiClient.delete(`/websites/${id}`);
    queryClient.invalidateQueries({ queryKey: ['WEBSITES'] });
  };

  const setServerContainerUrl = async (websiteId: number, url: string) => {
    await apiClient.put(`/websites/${websiteId}`, { serverContainerUrl: url });
    queryClient.invalidateQueries({ queryKey: ['CLIENT_ACCOUNTS'] });
  };

  const recheckPermissions = async (ganPropertyId: number) => {
    setRechecking(true);
    await ganClient.get(`/google-oauth/recheck-permissions/${ganPropertyId}`);
    queryClient.invalidateQueries({ queryKey: ['WEBSITES'] });
    setRechecking(false);
  };

  return {
    websites: organization?.websites,
    // The owning account's learnings contribution, for the website settings modal: a
    // website's own switch is overridden while its account has opted out.
    accountOptedOut: organization?.learningsContribution === 'opted-out',
    website: organization?.websites?.find((ws: any) => ws.id === +website),
    options,
    rechecking,
    setWebsite,
    createWebsite,
    refreshPropertyTags,
    setServerContainerUrl,
    deleteWebsite,
    recheckPermissions,
  };
};
